export interface ZendeskConversationField {
	id: string;
	value: string;
}

export const ZENDESK_FIELD_IDS = {
	userName: "49128817541147",
	requesterName: "50077802337435",
	requesterEmail: "50077842762267",
	dashPermissions: "50072083680283",
	orgLogin: "50077770100251",
	organizationId: "49128501829531",
	organizationName: "48757383658651",
	placeId: "49128731886619",
	placeName: "49128711570587",
	eventId: "49128623254939",
	eventName: "49128614519707",
	source: "49128446589979",
	isKeyAccount: "49785057294491",
	placeSize: "49785105216283",
	placeType: "49785751098267",
} as const;

export type ZendeskIsKeyAccountValue = "sim_cliente" | "não_cliente";

export type ZendeskPlaceSizeValue =
	| "pp_tamanho_casa"
	| "p_tamanho_casa"
	| "m_tamanho_casa"
	| "g_tamanho_casa"
	| "mega_tamanho_casa"
	| "não_identificado_tamanho_casa";

export type ZendeskPlaceTypeValue =
	| "sim_casa_evento"
	| "não_casa_evento"
	| "não_se_aplica_evento";

const ZENDESK_WIDGET_KEY = process.env.ZENDESK_WIDGET_KEY;

let loadPromise: Promise<void> | null = null;

let openIntent = false;

let authTokenRef: string | null = null;

let isLoginUserRegistered = false;

function registerLoginUser(zE: Function) {
	if (isLoginUserRegistered) return;
	zE("messenger", "loginUser", (callback: (token: string) => void) => {
		if (authTokenRef) callback(authTokenRef);
	});
	isLoginUserRegistered = true;
}

function getZE(): Function | undefined {
	return ((window as unknown) as Record<string, Function>).zE;
}

function loadZendeskScript(): Promise<void> {
	if (!ZENDESK_WIDGET_KEY) return Promise.resolve();
	if (window.Zendesk) return Promise.resolve();
	if (loadPromise) return loadPromise;

	loadPromise = new Promise((resolve, reject) => {
		const settings = document.createElement("script");
		settings.textContent = `
			window.zESettings = {
				messenger: { launcher: { visible: false } }
			};
		`;
		document.head.appendChild(settings);

		const script = document.createElement("script");
		script.id = "ze-snippet";
		script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_WIDGET_KEY}`;
		script.defer = true;
		const style = document.createElement("style");
		style.textContent = `
			iframe#launcher,
			iframe[title*="launch" i],
			iframe[title*="abrir" i],
			iframe[title*="Botão" i],
			iframe[title*="Button to" i],
			iframe[name*="launch" i] { display: none !important; }
		`;
		document.head.appendChild(style);

		script.onload = () => {
			const zE = getZE()!;

			zE("messenger:set", "cookies", true);
			zE("messenger", "hide");
			zE("messenger:on", "close", () => zE("messenger", "hide"));

			window.Zendesk = {
				open: () => zE("messenger", "open"),
				close: () => zE("messenger", "close"),
				set: (field, value) => zE("messenger:set", field, value),
			};
			resolve();
		};
		script.onerror = () => {
			loadPromise = null;
			reject(new Error("Zendesk script failed to load"));
		};
		document.head.appendChild(script);
	});

	return loadPromise;
}

export function setZendeskAuthToken(jwt: string) {
	authTokenRef = jwt;
}

export function logoutZendesk() {
	if (!isLoginUserRegistered) return;
	authTokenRef = null;
	isLoginUserRegistered = false;
	const zE = getZE();
	if (zE) {
		zE("messenger", "logoutUser");
	}
}

export function setZendeskOpenIntent(value: boolean) {
	openIntent = value;
}

export async function openZendeskChat(fields?: ZendeskConversationField[]) {
	await loadZendeskScript();

	if (!openIntent) return;

	const zE = getZE();
	if (!zE) return;

	if (authTokenRef) registerLoginUser(zE);

	if (fields?.length) {
		zE("messenger:set", "conversationFields", fields);
	}
	zE("messenger", "show");
	zE("messenger", "open");
}

export function closeZendeskChat() {
	openIntent = false;
	const zE = getZE();
	if (zE) {
		zE("messenger", "close");
		zE("messenger", "hide");
	}
}
